import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Camera, X, Barcode, Keyboard } from "lucide-react";

/**
 * BarcodeScanner Component
 * Supports both USB barcode scanners and camera scanning
 * 
 * USB Scanner: Works automatically - just focus the input and scan
 * Camera: Uses device camera to scan barcodes (requires https in production)
 */
export default function BarcodeScanner({ onScan, placeholder = "Escanear código de barras...", autoFocus = true }) {
  const [value, setValue] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lastKeyTime = useRef(0);
  const keyBuffer = useRef("");

  // Handle USB barcode scanner input
  // Barcode scanners typically send characters very fast followed by Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only process if input is focused
      if (document.activeElement !== inputRef.current) return;
      
      const now = Date.now();
      const timeDiff = now - lastKeyTime.current;
      
      // If more than 100ms since last key, reset buffer (manual typing)
      if (timeDiff > 100) {
        keyBuffer.current = "";
      }
      
      lastKeyTime.current = now;
      
      // If Enter key and we have a buffer, it's likely a scanner
      if (e.key === "Enter" && keyBuffer.current.length > 3) {
        e.preventDefault();
        const scannedCode = keyBuffer.current;
        keyBuffer.current = "";
        setValue(scannedCode);
        onScan?.(scannedCode);
      } else if (e.key.length === 1) {
        keyBuffer.current += e.key;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onScan]);

  // Auto focus on mount
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle manual input
  const handleInputChange = (e) => {
    setValue(e.target.value);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && value.trim()) {
      onScan?.(value.trim());
    }
  };

  // Camera scanning with BarcodeDetector API (if available)
  const startCamera = async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Use BarcodeDetector if available
      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({
          formats: ["ean_13", "ean_8", "code_128", "code_39", "qr_code", "upc_a", "upc_e"]
        });
        
        const detectBarcode = async () => {
          if (!videoRef.current || !showCamera) return;
          
          try {
            const barcodes = await detector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const code = barcodes[0].rawValue;
              stopCamera();
              setValue(code);
              onScan?.(code);
              return;
            }
          } catch (err) {
            // Detection failed, continue trying
          }
          
          if (showCamera) {
            requestAnimationFrame(detectBarcode);
          }
        };
        
        detectBarcode();
      } else {
        setCameraError("Tu navegador no soporta la detección de códigos de barras. Usa un escáner USB o ingresa el código manualmente.");
      }
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError("No se pudo acceder a la cámara. Verifica los permisos.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  useEffect(() => {
    if (showCamera) {
      startCamera();
    }
    return () => stopCamera();
  }, [showCamera]);

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            className="pl-10 pr-10 font-mono"
            data-testid="barcode-input"
          />
          {value && (
            <button
              onClick={() => setValue("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setShowCamera(true)}
          title="Escanear con cámara"
        >
          <Camera className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="default"
          onClick={() => value.trim() && onScan?.(value.trim())}
          disabled={!value.trim()}
        >
          Buscar
        </Button>
      </div>

      <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
        <Keyboard className="w-3 h-3" />
        Escanea con tu lector USB o ingresa el código manualmente
      </p>

      {/* Camera Dialog */}
      <Dialog open={showCamera} onOpenChange={(open) => !open && stopCamera()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Escanear Código de Barras
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
            />
            {/* Scanning overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-64 h-32 border-2 border-green-500 rounded-lg relative">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-green-500" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-green-500" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-green-500" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-green-500" />
                {/* Scanning line animation */}
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-500 animate-scan" />
              </div>
            </div>
          </div>

          {cameraError && (
            <p className="text-sm text-red-500 text-center">{cameraError}</p>
          )}

          <p className="text-sm text-slate-500 text-center">
            Apunta la cámara al código de barras
          </p>

          <Button variant="outline" onClick={stopCamera}>
            Cancelar
          </Button>
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes scan {
          0%, 100% { top: 0; }
          50% { top: calc(100% - 2px); }
        }
        .animate-scan {
          animation: scan 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
